import {
  type Accessor,
  createContext,
  createMemo,
  createSignal,
  type ParentComponent,
  type Setter,
  useContext,
} from "solid-js";
import type { AvailabilitySource } from "@/domains/freelance/availability/constants/tracking";
import { useMediaQuery } from "@/hooks/useMediaQuery";

/**
 * Tailwind's `lg`, expressed in the same `rem` unit Tailwind uses, so the JS
 * side and the `lg:` utilities can never switch at different widths.
 */
const DESKTOP_QUERY = "(min-width: 64rem)";

export const LayoutContext = createContext<{
  isMobileMenuOpen: Accessor<boolean>;
  setIsMobileMenuOpen: Setter<boolean>;
  isDesktop: Accessor<boolean>;
  isSupportModalOpen: Accessor<boolean>;
  setIsSupportModalOpen: Setter<boolean>;
  isAvailabilityModalOpen: Accessor<boolean>;
  setIsAvailabilityModalOpen: Setter<boolean>;
  availabilityModalSource: Accessor<AvailabilitySource | null>;
  openAvailabilityModal: (source: AvailabilitySource) => void;
  isInviteFreelancerModalOpen: Accessor<boolean>;
  setIsInviteFreelancerModalOpen: Setter<boolean>;
  toggleMobileMenu: () => void;
  handleOpenDonation: () => void;
  isSidebarCollapsed: Accessor<boolean>;
  toggleSidebarCollapsed: () => void;
}>();

type LayoutProviderProps = {
  sidebarStorageKey?: string;
};

export const LayoutProvider: ParentComponent<LayoutProviderProps> = (props) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = createSignal(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = createSignal(false);
  const [isAvailabilityModalOpen, setIsAvailabilityModalOpen] =
    createSignal(false);
  const [availabilityModalSource, setAvailabilityModalSource] =
    createSignal<AvailabilitySource | null>(null);
  const [isInviteFreelancerModalOpen, setIsInviteFreelancerModalOpen] =
    createSignal(false);
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const [isSidebarCollapsedPreference, setIsSidebarCollapsedPreference] =
    createSignal(
      !!props.sidebarStorageKey &&
        localStorage.getItem(props.sidebarStorageKey) === "true",
    );

  /**
   * The collapsed icon rail only exists from `lg` up: below that breakpoint the
   * same `<aside>` is a full-width off-canvas drawer, and the collapse toggle is
   * `hidden lg:flex`. Gating the stored preference on the breakpoint stops a
   * choice made at a desk from following the user to their phone, where it would
   * open a 64px drawer with no control to expand it again.
   */
  const isSidebarCollapsed = () =>
    isDesktop() && isSidebarCollapsedPreference();

  /**
   * The only way to open the availability modal, so the save event can say which
   * surface moved the freelancer to act (R6). Closing keeps the setter and leaves
   * the source alone: the save reads it before the modal closes, and clearing it
   * from `onOpenChange` would be a race the event would lose without a trace.
   */
  const openAvailabilityModal = (source: AvailabilitySource) => {
    setAvailabilityModalSource(source);
    setIsAvailabilityModalOpen(true);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen());
  };

  const toggleSidebarCollapsed = () => {
    const next = !isSidebarCollapsedPreference();
    setIsSidebarCollapsedPreference(next);
    if (props.sidebarStorageKey) {
      localStorage.setItem(props.sidebarStorageKey, String(next));
    }
  };

  const handleOpenDonation = () => {
    const url = import.meta.env.VITE_PUBLIC_DONATION_URL;
    window.open(url, "_blank");
  };
  const layoutContext = createMemo(() => ({
    isMobileMenuOpen,
    setIsMobileMenuOpen,
    isDesktop,
    isSupportModalOpen,
    setIsSupportModalOpen,
    isAvailabilityModalOpen,
    setIsAvailabilityModalOpen,
    availabilityModalSource,
    openAvailabilityModal,
    isInviteFreelancerModalOpen,
    setIsInviteFreelancerModalOpen,
    toggleMobileMenu,
    handleOpenDonation,
    isSidebarCollapsed,
    toggleSidebarCollapsed,
  }));
  return (
    <LayoutContext.Provider value={layoutContext()}>
      {props.children}
    </LayoutContext.Provider>
  );
};

export const useLayoutContext = () => {
  const layoutContext = useContext(LayoutContext);
  if (!layoutContext) {
    throw new Error("useLayoutContext must be used within a LayoutProvider");
  }
  return layoutContext;
};
